<template>
  <nav
    class="flex-1 desktop-side-menu"
    aria-label="Sidebar"
  >
    <template
      v-for="item in navigation"
      class="items-center"
      :key="item.name"
    >
      <template v-if="rowIsVisible(item)">
        <Link
          v-if="!item.children && !item.onClick"
          :href="item.href"
          :class="[
            isCurrentPage(item)
              ? 'text-gray-100 bg-navy-600 current-page rounded-r-lg'
              : 'rounded-r-lg text-gray-100 hover:bg-navy-600 ',
            !item.icon ? 'h-14 py-3 px-10' : 'h-14 py-3 px-10',
            'group w-full flex items-center text-base font-semibold transition-all mb-1'
          ]"
        >
          <component
            v-if="item.icon"
            :is="item.icon"
            :class="['text-navy-200 shrink-0 h-5 w-5 mr-6']"
          />
          <span>
            {{ item.name }}
          </span>
        </Link>
        <a
          v-else-if="!item.children"
          href="#"
          @click.prevent="item.onClick"
          :class="[
            isCurrentPage(item)
              ? 'text-gray-100 bg-navy-600 current-page rounded-r-lg'
              : 'rounded-r-lg text-gray-100 hover:text-navy-200 hover:bg-navy-600 ',
            !item.icon ? 'h-14 py-3 px-10' : 'h-14 py-3 px-10',
            'group w-full flex items-center text-base font-semibold transition-all mb-1 bg-navy-600'
          ]"
        >
          <component
            v-if="item.icon"
            :is="item.icon"
            :class="['text-navy-200 shrink-0 h-5 w-5 mr-6']"
          />
          <span>
            {{ item.name }}
          </span>
        </a>
        <Disclosure
          as="div"
          v-else
          class=""
          v-slot="{ open }"
          :default-open="isCurrentPage(item)"
        >
          <DisclosureButton
            :class="[
              open || isCurrentPage(item)
                ? 'text-gray-100 bg-navy-600 hover:bg-navy-600 rounded-r-lg'
                : 'rounded-r-lg text-gray-100 hover:text-gray-100 hover:bg-navy-600 font-semibold ',
              'font-semibold group w-full flex items-left text-left text-base h-14 py-3 px-10 transition-all  '
            ]"
          >
            <component
              v-if="item.icon"
              :is="item.icon"
              :class="['text-navy-200 shrink-0 h-5 w-5 mr-6 align-center self-center']"
            />

            <div class="flex flex-row justify-between w-full align-center self-center">
              <span :class="[!item.icon ? 'pl-0' : 'pl-0']">
                {{ item.name }}
              </span>

              <svg
                class="right-3 absolute h-5 w-5 align-center self-center"
                :class="[isCurrentPage(item) ? 'fill-current text-gray-100' : '']"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fill-rule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </DisclosureButton>
          <transition
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="scale-95 opacity-0"
            enter-to-class="scale-100 opacity-100"
            leave-active-class="transition duration-75 ease-out"
            leave-from-class="scale-100 opacity-100"
            leave-to-class="scale-95 opacity-0"
          >
            <DisclosurePanel class="-mt-4 mb-4 py-2 pb-4 rounded-br-lg bg-navy-600">
              <template
                v-for="subItem in item.children"
                :key="subItem.name"
              >
                <Link
                  v-if="rowIsVisible(subItem) && !rowIsFullPageLoad(subItem)"
                  :href="subItem.href"
                  :class="[
                    isCurrentPage(subItem) ? 'text-gray-100' : 'text-gray-500',
                    'group w-full flex items-center text-sm font-semibold hover:text-gray-100 pl-24 py-0.5 '
                  ]"
                >
                  <span class="-ml-3">
                    {{ subItem.name }}
                  </span>
                </Link>
                <a
                  v-if="rowIsVisible(subItem) && rowIsFullPageLoad(subItem)"
                  :href="subItem.href"
                  :class="[
                    isCurrentPage(subItem) ? 'text-gray-100' : 'text-gray-500',
                    'group w-full flex items-center text-sm font-semibold hover:text-gray-100 pl-24 py-0.5 '
                  ]"
                >
                  <span class="-ml-3">
                    {{ subItem.name }}
                  </span>
                </a>
              </template>
            </DisclosurePanel>
          </transition>
        </Disclosure>
      </template>
    </template>
  </nav>
</template>

<script>
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { Link } from '@inertiajs/vue3'
import hasAuth from '@/Mixins/hasAuth'
import generateNavigation from '@/Layouts/Components/generateNavigation'

export default {
  mixins: [hasAuth],

  components: { Disclosure, DisclosureButton, DisclosurePanel, Link },

  computed: {
    /**
     * Returns an array of paths that should be included in this sidebar
     * @returns Array
     */
    navigation() {
      return generateNavigation(route, this.$page.props.auth)
    }
  },

  methods: {
    /**
     * Checks if this row should be visible. If row.visible = false row will not be displayed
     * @param object row the row object to check
     * @returns boolean
     */
    rowIsVisible(row) {
      return row?.visible !== undefined ? row.visible : true
    },
    rowIsFullPageLoad(row) {
      return row?.isFullPageLoad !== undefined ? row.isFullPageLoad : false
    },

    /**
     * Returns true if the provided row represents the current page/route of the application
     * @param row
     * @returns {boolean}
     */
    isCurrentPage(row) {
      const hasCurrentChildRoute = (row) => {
        if (!row.childRoutes || !Array.isArray(row.childRoutes)) {
          return false
        }

        return row.childRoutes.reduce((isCurrent, childRoute) => {
          return isCurrent || childRoute === route().current()
        }, false)
      }

      const isCurrentRow = (row) => {
        const rowIsCurrent = row.route === route().current()
        const children = Array.isArray(row.children) ? row.children : []

        return children.reduce(
          (isCurrent, childRow) => {
            return isCurrent || isCurrentRow(childRow)
          },
          rowIsCurrent || hasCurrentChildRoute(row)
        )
      }

      return isCurrentRow(row)
    }
  }
}
</script>
